/* The composer owns unsent words. The agent line opens it through this connection and reads its
 * state without touching storage. Asynchronous saves acknowledge only the originating snapshot,
 * including after a responsive remount or navigation to another conversation.
 */
import { generateHTML, generateJSON } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useState, useSyncExternalStore } from "react";

/** What the box holds and shows. */
export type BoxContent = {
  readonly format: "text" | "html";
  /** The words; in Plain text, exactly what is sent. */
  readonly text: string;
  /** The editor's markup in Formatted; Plain text as the paragraphs it is saved as. */
  readonly html: string;
  /** Whether the box is open: the sheet on a desktop, the tray on a phone. */
  readonly open: boolean;
  /** The draft revision "Edit in reply box" last put in it, edited since or not; null for none. */
  readonly loaded: number | null;
  /** The box's files, where it has any or had a draft's taken off; absent otherwise. */
  readonly files?: BoxFiles;
};

/** The box's files as the card's Approve names them (`use-reply-files.ts`). */
export type BoxFiles = {
  readonly composerId: string;
  readonly revision: number;
  readonly fileIds: readonly string[];
  /** Different from the files the loaded draft brought in: one added or taken off. */
  readonly edited: boolean;
  /** Nothing is still uploading and nothing was refused. */
  readonly ready: boolean;
};

const sameFiles = (a: BoxFiles | undefined, b: BoxFiles | undefined) =>
  a === b ||
  (a !== undefined &&
    b !== undefined &&
    a.composerId === b.composerId &&
    a.revision === b.revision &&
    a.edited === b.edited &&
    a.ready === b.ready &&
    a.fileIds.join() === b.fileIds.join());

/** A draft revision as the box takes it whole: both forms of the email, ending included. */
export type WholeDraft = {
  readonly revision: number;
  readonly html: string;
  readonly plainText: string;
};

const EMPTY: BoxContent = { format: "text", text: "", html: "", open: false, loaded: null };

type Connection = {
  readonly planId: string;
  readonly read: () => BoxContent;
  readonly edit: () => void;
  /** The box's words became this draft revision on the server, as Approve saved them. */
  readonly adopt: (snapshot: BoxContent, revision: number) => void;
  /** The box's words were saved as this whole revision ("Change ending"): put it in the box. */
  readonly replace: (snapshot: BoxContent, draft: WholeDraft) => void;
};

/** Content identity, independent of whether the sheet was folded while a request was pending. */
export const sameBoxText = (a: BoxContent, b: BoxContent): boolean =>
  a.format === b.format && a.text === b.text && a.html === b.html && a.loaded === b.loaded;

const same = (a: BoxContent, b: BoxContent) =>
  a.format === b.format &&
  a.text === b.text &&
  a.html === b.html &&
  a.open === b.open &&
  a.loaded === b.loaded &&
  sameFiles(a.files, b.files);

export class ReplyBoxLink {
  #connection: Connection | null = null;
  #content: BoxContent = EMPTY;
  readonly #listeners = new Set<() => void>();
  readonly #approving = new Set<string>();

  /** The box, while it is drawn; the returned function lets it go. */
  connect(connection: Connection): () => void {
    this.#connection = connection;
    return () => {
      if (this.#connection === connection) this.#connection = null;
    };
  }

  /** What the box holds now, read when asked; null while no box is drawn. */
  read(): BoxContent | null {
    return this.#connection?.read() ?? null;
  }

  /** Opens the box on the draft, asking first over the rep's own words (`reply-box.tsx`). */
  edit(): void {
    this.#connection?.edit();
  }

  /**
   * Capture the originating connection and exact content before any asynchronous work: an approval,
   * or the box's change of ending. Either freezes the box until it is released.
   */
  begin(planId: string) {
    if (this.#approving.has(planId))
      throw new Error("The reply box is still saving this draft. Try again in a moment.");
    const origin = this.#connection?.planId === planId ? this.#connection : null;
    const snapshot = origin?.read() ?? null;
    this.#approving.add(planId);
    this.#emit();
    return {
      content: snapshot,
      saved: (revision: number) => {
        if (!snapshot || !origin) return;
        origin.adopt(snapshot, revision);
        // Returning to the same conversation while its save was in flight creates a new box.
        const active = this.#connection;
        if (active && active !== origin && active.planId === planId)
          active.adopt(snapshot, revision);
      },
      /** As `saved`, for a save that changed more than the box held: the revision goes in whole. */
      replaced: (draft: WholeDraft) => {
        if (!snapshot || !origin) return;
        origin.replace(snapshot, draft);
        const active = this.#connection;
        if (active && active !== origin && active.planId === planId)
          active.replace(snapshot, draft);
      },
      release: () => {
        this.#approving.delete(planId);
        this.#emit();
      },
    };
  }

  approving(planId: string): boolean {
    return this.#approving.has(planId);
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }
  publish(content: BoxContent): void {
    if (same(content, this.#content)) return;
    this.#content = content;
    this.#emit();
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  readonly content = (): BoxContent => this.#content;
}

/** One link per open conversation's reading pane. */
export function useReplyBoxLink(): ReplyBoxLink {
  return useState(() => new ReplyBoxLink())[0];
}

/** What the box holds as it changes, for a card that says what Approve will do with it. */
export function useBoxContent(link: ReplyBoxLink): BoxContent {
  return useSyncExternalStore(link.subscribe, link.content);
}

/** Approval freezes only its originating conversation, including a return to it while pending. */
export function useBoxApproving(link: ReplyBoxLink | null, planId: string): boolean {
  return useSyncExternalStore(
    link?.subscribe ?? (() => () => undefined),
    () => link?.approving(planId) ?? false,
  );
}

/** The reply box's editor, so a draft's markup can be read exactly as the box would hold it. */
export const REPLY_EXTENSIONS = [
  StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
];

let normalized: { html: string; editor: string } | null = null;

/** Markup as the box's editor writes it once loaded: the form a Formatted box is compared in. */
export function editorHtml(html: string): string {
  if (normalized?.html !== html)
    normalized = {
      html,
      editor: generateHTML(generateJSON(html, REPLY_EXTENSIONS), REPLY_EXTENSIONS),
    };
  return normalized.editor;
}

/** Plain text as the editor's paragraphs: a blank line parts them, a single break stays a break. */
export const paragraphs = (plain: string): string =>
  plain
    .split(/\n{2,}/u)
    .map(
      (part) =>
        `<p>${part.replace(/[<>&]/gu, (c) => `&#${c.charCodeAt(0)};`).replace(/\n/gu, "<br>")}</p>`,
    )
    .join("");

/**
 * Whether the box holds something other than this email as it stands, compared in the box's own
 * format: Formatted by its markup, so a bold or a link on the same words counts; Plain text by its
 * text. An empty box holds nothing.
 */
export function holdsOtherThan(
  box: Pick<BoxContent, "format" | "text" | "html">,
  email: { readonly html: string; readonly plainText: string },
): boolean {
  if (box.text.trim() === "") return false;
  return box.format === "html"
    ? box.html !== editorHtml(email.html)
    : box.text.trim() !== email.plainText.trim();
}
